import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import CurrencyFormat from 'react-currency-format';
import { ProgramsModal, TruncatedText, Tooltip, Hyphen, InfoTooltipLink } from 'components';
import _ from 'lodash';
import { FaTimes, FaInfoCircle } from 'react-icons/fa';
import nologo from 'images/snapp-icon.png';
import { I18nContext } from 'i18n';
import { STATUS_VERIFIED, DEMOGRAPHICS_ORDER } from 'utils/constants'
import { useCode } from 'hooks/useCode'

const PeerReport = ({ peers, criterias, onRemove, title, reportType }) => {
  const { statuses, getStatuses } = useCode()
  const { translate } = useContext(I18nContext);
  const [show, setShow] = useState();
  const [org, setOrg] = useState();
  const checkRow = (value) => {
    return criterias?.some((e) => e.value === value);
  };

  useEffect(() => {
    getStatuses()
  }, [])

  const getVerifiedStatus = (statuses) =>
    statuses?.find(({ name }) => name === STATUS_VERIFIED)?.id

  const verifiedStatusId = useMemo(
    () => getVerifiedStatus(statuses),
    [statuses]
  )

  const handleClick = ({ id }) => {
    setOrg(id);
    setShow(true);
  };

  const onHide = () => {
    setShow(false);
    setOrg(null);
  };

  const bothPresent = (min, max) => {
    if ((min && max) || (!min && !max)) return null;

    return '';
  }

  const renderDemographics = (demographics) => {
    const newDemo = demographics.split(',').map((demo) => {
      const [title, value] = demo.split(' - ');
      const label = title.trim();

      return { label, value, order: DEMOGRAPHICS_ORDER.indexOf(label) };
    });

    const grouped = _(newDemo)
      .orderBy((o) => o.order)
      .groupBy((x) => x.label)
      .map((value, key) => ({ label: key, value }))
      .value();

    return (
      <div>
        {grouped.map(({ label, value }) => (
          <div key={label}>
            <Strong>{label && `${label}: `}</Strong>
            {value.map((item, ix) => (
              <span key={ix}>
                {item.value}
                {value.length !== ix + 1 && ', '}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderStrong = (item, currency = false) => {
    const [label, value] = item.split(':');
    if (!value) return null
    const [min, max] = value?.split(' - ');

    const currencyValueMin = (
      <CurrencyFormat
        value={min}
        displayType={'text'}
        thousandSeparator={true}
        prefix={'$'}
      />
    );

    const currencyValueMax = (
      <CurrencyFormat
        value={max}
        displayType={'text'}
        thousandSeparator={true}
        prefix={' - $'}
      />
    );

    return (
      <>
        <Strong>
          {label && `${label.replace('Minimum', '').replace('Range', '')}: `}
        </Strong>
        {!currency && <span>{value}</span>}
        {currency && min && currencyValueMin}
        {currency && max && currencyValueMax}
        <br />
      </>
    );
  };

  const handleMoreClick = (cssClass) => {
    const elm = document.querySelectorAll(`.${cssClass} .badge`)
    elm.forEach((item) => item.click())
  }
  
  const renderReportCol = (value, item) => {
    const {
      organization: { peerComparisonFactor, type },
    } = item;

    switch (value) {
      case 'organizationType':
        // const { organizationType } = item;
        return <Col>{type || <Hyphen />}</Col>;
      case 'totalNumberStudents':
        const { totalNumberStudents } = item;
        return <Col>{totalNumberStudents || <Hyphen />}</Col>;
      case 'demographics':
        return (
          <Col>
            {peerComparisonFactor.demographics ? (
              renderDemographics(peerComparisonFactor?.demographics)
            ) : (
              <Hyphen />
            )}
          </Col>
        );
      case 'academics':
        return (
          <Col>
            {peerComparisonFactor?.academics.split(',').map((item, index) => (
              <React.Fragment key={index}>{renderStrong(item)}</React.Fragment>
            ))}
            {!peerComparisonFactor.academics && <Hyphen />}
          </Col>
        );
      case 'age':
        return (
          <Col>
            {peerComparisonFactor?.minAge}
            {peerComparisonFactor?.minAge && peerComparisonFactor?.maxAge && (
              <span>-</span>
            )}

            {!peerComparisonFactor?.minAge && peerComparisonFactor?.maxAge && (
              <span> {'<='} </span>
            )}
            {peerComparisonFactor?.maxAge}

            {peerComparisonFactor?.minAge && !peerComparisonFactor?.maxAge && (
              <span> and Older</span>
            )}

            {!peerComparisonFactor?.maxAge && !peerComparisonFactor?.minAge && (
              <Hyphen />
            )}
          </Col>
        );
      case 'financialInformation':
        return (
          <Col>
            {peerComparisonFactor?.financialInformation
              .split(',')
              .map((item, index) => (
                <React.Fragment key={index}>
                  {renderStrong(item, true)}
                </React.Fragment>
              ))}

            {!peerComparisonFactor.financialInformation && <Hyphen />}
          </Col>
        );
      case 'awardRange':
        return (
          <Col>
            {bothPresent(
              peerComparisonFactor?.scholarshipMinimumAward,
              peerComparisonFactor?.scholarshipMaximumAward
            )}
            {peerComparisonFactor?.scholarshipMinimumAward !== 0 &&
              peerComparisonFactor?.scholarshipMinimumAward !==
                peerComparisonFactor?.scholarshipMaximumAward && (
                <CurrencyFormat
                  value={peerComparisonFactor?.scholarshipMinimumAward}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                />
              )}
            {peerComparisonFactor?.scholarshipMinimumAward !== 0 &&
              peerComparisonFactor?.scholarshipMaximumAward !== 0 &&
              peerComparisonFactor?.scholarshipMinimumAward !==
                peerComparisonFactor?.scholarshipMaximumAward && (
                <span> - </span>
              )}
            {peerComparisonFactor?.scholarshipMaximumAward !== 0 && (
              <CurrencyFormat
                value={peerComparisonFactor?.scholarshipMaximumAward}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
              />
            )}{' '}
            {!peerComparisonFactor.scholarshipMinimumAward &&
              !peerComparisonFactor.scholarshipMaximumAward && <Hyphen />}
          </Col>
        );
      case 'totalOverhead':
      case 'totalPayroll':
      case 'totalDollarAmount':
        return (
          <Col>
            {item[value] ? (
              <CurrencyFormat
                value={item[value]}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
              />
            ) : (
              <Hyphen />
            )}
          </Col>
        );
      case 'staffCount':
      case 'yearFirstScholarship':
        return <Col>{item[value] || <Hyphen />}</Col>;
      case 'totalAwardAmount':
        return (
          <Col>
            {peerComparisonFactor?.totalAwardAmount ? (
              <CurrencyFormat
                value={peerComparisonFactor?.totalAwardAmount}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
              />
            ) : (
              <Hyphen />
            )}
          </Col>
        );
      default:
        return (
          <Col className={`${value}-helper`}>
            {peerComparisonFactor[value] ? (
              <>
                <TruncatedText
                  cssClass={`${value}-helper`}
                  onClick={(cssClass) => handleMoreClick(cssClass)}
                  className="d-print-none"
                >
                  {peerComparisonFactor[value]}
                </TruncatedText>
                {/* print version */}
                <div className="d-none d-print-block">
                  {peerComparisonFactor[value]}
                </div>
              </>
            ) : (
              <Hyphen />
            )}
          </Col>
        )
    }
  }

  const ReportCol = ({ value }) => {
    return (
      <PeerRow fixedWidth={peers?.length}>
        <Col>
          <Title>
            {translate(value)}{' '}
            {translate(`${value}_info`) !== `${value}_info` && (
              <InfoTooltipLink
                text={translate(`${value}_info`)} 
                className="info-link-icon d-print-none"
                placement="top"
              />
            )}
          </Title>
        </Col>

        {checkRow(value) &&
          peers?.map((item, ix) => (
            <React.Fragment key={ix}>
              {renderReportCol(value, item)}
            </React.Fragment>
          ))}
      </PeerRow>
    )
  }

  const renderCityState = (city, state) => {
    return (
      <>
        {city && state ? `${city}, ${state}` : city || state}
      </>
    )
  }

  return (
    // render as a table so browser can identify the footer on print
    <>
      <table>
        <tbody>
        <tr>
          <td>
            <div className="sticky-top">
              <div className="d-none d-print-block text-center m-2">
                <h3>{translate(title)}</h3>
                <h4>{`By ${reportType}`}</h4>
              </div>
              <OrgRow>
                <Col></Col>
                {peers &&
                  peers.map(({ organization }, ix) => (
                    <Centered key={ix}>
                      <Image
                        src={organization.logo || nologo}
                        onClick={() => handleClick(organization)}
                      />
                    </Centered>
                  ))}
              </OrgRow>

              <OrgRow>
                <Col></Col>
                {peers &&
                  peers.map(({ organization }, ix) => (
                    <Centered key={ix}>
                      <OrgName onClick={() => handleClick(organization)}>
                        {organization.name}
                      </OrgName>
                    </Centered>
                  ))}
              </OrgRow>
            </div>

            <CityRow>
              <Col></Col>
              {peers &&
                peers.map(({ city, stateAbbrev, organization }, ix) => (
                  <Centered key={ix}>
                    {renderCityState(city || organization.city, stateAbbrev || organization.stateCode)}
                  </Centered>
                ))}
            </CityRow>

            {criterias?.map(({ value }, ix) => (
              <ReportCol value={value} key={ix} />
            ))}

            {/* Delete row */}
            <PrintRow>
              <Col></Col>
              {peers &&
                peers.map((item, ix) => (
                  <Col key={ix}>
                    {ix !== 0 && (
                      <DeleteButton
                        onClick={() => onRemove(item)}
                        disabled={peers.length === 1}
                      >
                        <FaTimes /> REMOVE
                      </DeleteButton>
                    )}
                  </Col>
                ))}
            </PrintRow>

            {show && verifiedStatusId && (
              <ProgramsModal
                show={show}
                onHide={onHide}
                organizationId={org}
                statusId={verifiedStatusId}
              />
            )}
          </td>
        </tr>
        </tbody>
        <tfoot>
        <tr>
          {/* add empty footer to prevent overlap */}
          <td className="d-none d-print-block" style={{ height: '6rem' }}></td>
        </tr>
        </tfoot>
        {/* fixed footer */}
      </table>
      <PrintFooter className="d-none d-print-block">
        <p className="mb-1">Report powered by the NSPA Exchange</p>
        <p>
          &copy; {new Date().getFullYear()} National Scholarship Provider
          Association
        </p>
      </PrintFooter>
    </>
  )
}

const PrintFooter = styled.footer`
  position: fixed;
  bottom: 0;
  background-color: white;
  width: 100%;
  height: 5rem;
  padding-top: 1rem;
  color: #707070;
`

const DeleteButton = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.palette.grayscale[5]};
  color: ${({ theme }) => theme.palette.grayscale[1]};
  padding: 0.3rem 0.5rem;
  border-radius: 0.3rem;
  font-size: 0.8rem;

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const rowStyle = css`
  padding: 1rem;
  font-size: 0.9rem;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[5]};
`;

const PeerRow = styled(Row)`
  ${rowStyle}
  page-break-inside: avoid;
  ${({ fixedWidth }) => (
      fixedWidth === 4 ? css`width: 1150px;`
    : fixedWidth === 3 ? css`width: 900px;`
    : fixedWidth === 2 ? css`width: 750px;`
    : fixedWidth === 1 && css`width: 550px;`
  )}
`;

const PrintRow = styled(Row)`
  ${rowStyle}

  @media print {
    display: none;
  }
`;

const CityRow = styled(Row)`
  font-size: 0.9rem;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[3]};
  padding-bottom: 1rem;
`;

const OrgRow = styled(Row)`
  padding: 0.5rem;
  font-size: 0.9rem;
  background-color: ${({ theme }) => theme.palette.grayscale[8]};
`;

const Centered = styled(Col)`
  text-align: center;
`;

const Image = styled.img`
  height: 100px;
  cursor: pointer;
`;

const Title = styled.span`
  font-weight: bold;
`;

const OrgName = styled.span`
  font-weight: bold;
  cursor: pointer;
`;

const Strong = styled.span`
  font-weight: bold;
`;

export default PeerReport;
